import { useState, useCallback, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  IconButton,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';

interface VariableEditorProps {
  variables: Record<string, unknown>;
  onSave: (variables: Record<string, unknown>) => void;
  onCancel: () => void;
  isSaving?: boolean;
}

type VariableType = 'string' | 'number' | 'boolean' | 'json';

interface EditableVariable {
  key: string;
  value: string;
  type: VariableType;
  isNew?: boolean;
}

function inferType(value: unknown): VariableType {
  if (typeof value === 'boolean') return 'boolean';
  if (typeof value === 'number') return 'number';
  if (typeof value === 'object' && value !== null) return 'json';
  return 'string';
}

function parseValue(value: string, type: VariableType): unknown {
  switch (type) {
    case 'number':
      return parseFloat(value) || 0;
    case 'boolean':
      return value === 'true';
    case 'json':
      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    default:
      return value;
  }
}

function stringifyValue(value: unknown, type: VariableType): string {
  if (type === 'json') {
    return JSON.stringify(value, null, 2);
  }
  return String(value ?? '');
}

export function VariableEditor({ variables, onSave, onCancel, isSaving = false }: VariableEditorProps) {
  const [editableVars, setEditableVars] = useState<EditableVariable[]>([]);

  useEffect(() => {
    const vars: EditableVariable[] = Object.entries(variables).map(([key, value]) => {
      const type = inferType(value);
      return {
        key,
        value: stringifyValue(value, type),
        type,
      };
    });
    setEditableVars(vars);
  }, [variables]);

  const handleKeyChange = useCallback((index: number, newKey: string) => {
    setEditableVars((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], key: newKey };
      return updated;
    });
  }, []);

  const handleValueChange = useCallback((index: number, newValue: string) => {
    setEditableVars((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], value: newValue };
      return updated;
    });
  }, []);

  const handleTypeChange = useCallback((index: number, newType: VariableType) => {
    setEditableVars((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], type: newType };
      return updated;
    });
  }, []);

  const handleAdd = useCallback(() => {
    setEditableVars((prev) => [
      ...prev,
      { key: '', value: '', type: 'string', isNew: true },
    ]);
  }, []);

  const handleRemove = useCallback((index: number) => {
    setEditableVars((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSave = useCallback(() => {
    const result: Record<string, unknown> = {};
    editableVars.forEach((v) => {
      if (v.key.trim()) {
        result[v.key.trim()] = parseValue(v.value, v.type);
      }
    });
    onSave(result);
  }, [editableVars, onSave]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="subtitle2">Prozessvariablen bearbeiten</Typography>

      {editableVars.map((variable, index) => (
        <Paper key={index} variant="outlined" sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <TextField
              size="small"
              label="Variable"
              value={variable.key}
              onChange={(e) => handleKeyChange(index, e.target.value)}
              disabled={!variable.isNew}
              sx={{ width: 200 }}
            />

            <FormControl size="small" sx={{ width: 120 }}>
              <InputLabel>Typ</InputLabel>
              <Select
                value={variable.type}
                label="Typ"
                onChange={(e) => handleTypeChange(index, e.target.value as VariableType)}
              >
                <MenuItem value="string">Text</MenuItem>
                <MenuItem value="number">Zahl</MenuItem>
                <MenuItem value="boolean">Boolean</MenuItem>
                <MenuItem value="json">JSON</MenuItem>
              </Select>
            </FormControl>

            {variable.type === 'boolean' ? (
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel>Wert</InputLabel>
                <Select
                  value={variable.value}
                  label="Wert"
                  onChange={(e) => handleValueChange(index, e.target.value)}
                >
                  <MenuItem value="true">Ja (true)</MenuItem>
                  <MenuItem value="false">Nein (false)</MenuItem>
                </Select>
              </FormControl>
            ) : variable.type === 'json' ? (
              <TextField
                size="small"
                label="Wert (JSON)"
                value={variable.value}
                onChange={(e) => handleValueChange(index, e.target.value)}
                multiline
                rows={3}
                sx={{ flex: 1 }}
              />
            ) : (
              <TextField
                size="small"
                label="Wert"
                value={variable.value}
                onChange={(e) => handleValueChange(index, e.target.value)}
                type={variable.type === 'number' ? 'number' : 'text'}
                sx={{ flex: 1 }}
              />
            )}

            <IconButton
              size="small"
              color="error"
              onClick={() => handleRemove(index)}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
          {variable.isNew && (
            <Chip label="Neu" size="small" color="info" sx={{ mt: 1 }} />
          )}
        </Paper>
      ))}

      <Button startIcon={<AddIcon />} onClick={handleAdd} variant="outlined" size="small">
        Variable hinzufuegen
      </Button>

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
        <Button startIcon={<CancelIcon />} onClick={onCancel} disabled={isSaving}>
          Abbrechen
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? 'Speichern...' : 'Speichern'}
        </Button>
      </Box>
    </Box>
  );
}
